import type { ReactNode } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  Divider,
  Theme,
  CircularProgress,
  Fade,
  type SxProps,
} from '@mui/material';
import { Close, ArrowBack } from '@mui/icons-material';

interface DrawerWrapperProps {
  open: boolean;
  onClose: () => void;
  title: string | ReactNode;
  children: ReactNode;
  actions?: ReactNode | ReactNode[];
  loading?: boolean;
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  sx?: SxProps;
}

const DrawerWrapper = ({
  open,
  onClose,
  title,
  children,
  actions,
  loading = false,
  showBackButton = false,
  onBackButtonClick,
  sx,
}: DrawerWrapperProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={sx}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: isMobile ? '100%' : '35%',
            minWidth: isMobile ? 'auto' : 600,
            padding: isMobile ? 1 : 2,
            margin: isMobile ? 0 : 1,
            borderRadius: isMobile ? 0 : 2,
            height: isMobile ? '100%' : 'calc(100% - 16px)',
            background: theme.palette.background.paper,
          }),
        },
      }}
    >
      <Fade in={loading}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      </Fade>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          position: 'relative',
          height: 48,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            transition: 'transform 0.1s ease-in-out',
            transform: showBackButton ? 'translateX(0)' : 'translateX(-48px)',
          }}
        >
          <IconButton
            onClick={onBackButtonClick}
            sx={{
              opacity: showBackButton ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            fontSize: '1.4rem',
            transition: 'transform 0.1s ease-in-out',
            transform: showBackButton ? 'translateX(48px)' : 'translateX(0)',
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box
        sx={{
          height: 'calc(100% - 64px)',
          overflowY: 'auto',
          padding: 1,
        }}
      >
        {children}
      </Box>
      {actions && (
        <>
          <Divider sx={{ mt: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            {actions}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default DrawerWrapper;
